import defaultRoutes from '../../config/routes';

export interface RouteConfig {
  path?: string;
  name?: string;
  icon?: string;
  component?: string;
  exact?: boolean;
  routes?: RouteConfig[];
  redirect?: string;
  hideInMenu?: boolean;
  layout?: boolean;
  [key: string]: any;
}

export interface MenuRouteInfo {
  visibleKeys: Set<string>;
  redirects: Record<string, string>;
}

/**
 * Extracts visible menu keys and redirects from a route configuration tree.
 * Visible menu keys correspond to visible clickable menu items in the sidebar.
 */
export function extractMenuRouteInfo(routes: RouteConfig[]): MenuRouteInfo {
  const visibleKeys = new Set<string>();
  const redirects: Record<string, string> = {};

  function traverse(items: RouteConfig[], parentKey?: string) {
    for (const item of items) {
      if (item.layout === false) {
        continue;
      }
      if (item.redirect && item.path) {
        redirects[item.path] = item.redirect;
        continue;
      }
      if (item.hideInMenu) {
        continue;
      }
      if (item.path && !item.path.includes(':') && !item.path.includes('*')) {
        // A route is a visible menu item if it has a component, or is a leaf route with a name,
        // or has child routes but also acts as an explicit page route.
        if (item.component || !item.routes || item.routes.length === 0) {
          visibleKeys.add(item.path);
        }
      }
      if (item.routes && item.routes.length > 0) {
        traverse(item.routes, item.path || parentKey);
      }
    }
  }

  traverse(routes);
  return { visibleKeys, redirects };
}

// Cache default menu route info for performance
let defaultMenuInfo: MenuRouteInfo | null = null;
function getDefaultMenuInfo(): MenuRouteInfo {
  if (!defaultMenuInfo) {
    defaultMenuInfo = extractMenuRouteInfo(defaultRoutes as RouteConfig[]);
  }
  return defaultMenuInfo;
}

/**
 * Computes the selected menu keys for the given pathname.
 *
 * Rules:
 * 1. Normalize pathname by stripping query string, hash, and trailing slashes.
 * 2. Follow redirects if the path is a configured redirect.
 * 3. Exact match on visible static menu items has highest priority -> returns [exactPath].
 * 4. Dynamic/hidden routes (e.g. /messages/:messageId, /workflow/:flowId, /access/partners/:partnerId)
 *    fall back to the longest matching visible static parent/prefix route.
 *
 * @param pathname Current router pathname (e.g. "/messages/documents", "/messages/IN20260923000006")
 * @param routes Optional custom routes config (defaults to config/routes.ts)
 * @returns Array containing the single matching menu key (or empty array if no match)
 */
export function computeSelectedMenuKeys(
  pathname: string,
  routes?: RouteConfig[],
): string[] {
  if (!pathname) {
    return [];
  }

  const { visibleKeys, redirects } = routes
    ? extractMenuRouteInfo(routes)
    : getDefaultMenuInfo();

  // 1. Normalize pathname: strip query string, hash, and trailing slash
  let normalizedPath = pathname.split('?')[0].split('#')[0].trim();
  if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  // 2. Follow redirects (e.g. "/" -> "/dashboard", "/access" -> "/access/partners")
  const visited = new Set<string>();
  while (redirects[normalizedPath] && !visited.has(normalizedPath)) {
    visited.add(normalizedPath);
    normalizedPath = redirects[normalizedPath];
  }

  // 3. Exact match on visible static menu items
  if (visibleKeys.has(normalizedPath)) {
    return [normalizedPath];
  }

  // 4. Fallback for dynamic/hidden child paths: find the longest matching visible static prefix
  let bestMatch: string | null = null;
  for (const vKey of visibleKeys) {
    if (normalizedPath.startsWith(`${vKey}/`)) {
      if (!bestMatch || vKey.length > bestMatch.length) {
        bestMatch = vKey;
      }
    }
  }

  if (bestMatch) {
    return [bestMatch];
  }

  return [];
}
